import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Avatar,
  Button,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  FaCamera,
  FaTrash,
  FaUpload,
  FaUser
} from 'react-icons/fa';
import './ProfilePhotoUploader.css';
import userService from '../utils/userService';
import authService from '../utils/authService';
import { useDispatch } from 'react-redux';
import { getCurrentUser } from '../redux/slices/authSlice';

const ProfilePhotoUploader = ({ currentPhoto, onPhotoChange }) => {
  const dispatch = useDispatch();
  const [preview, setPreview] = useState(currentPhoto?.url || currentPhoto || null);
  const [isUploading, setIsUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  
  useEffect(() => {
    setPreview(currentPhoto?.url || currentPhoto || null);
  }, [currentPhoto]);

  // Formats d'image acceptés
  const acceptedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const maxFileSize = 5 * 1024 * 1024; // 5MB
  
  // Gérer la sélection de fichier
  const handleFileSelect = (file) => {
    if (!file) return;
    
    // Validation du format
    if (!acceptedFormats.includes(file.type)) {
      alert('Format non supporté. Veuillez utiliser JPG, PNG ou WebP.');
      return;
    }
    
    // Validation de la taille
    if (file.size > maxFileSize) {
      alert('Le fichier est trop volumineux. Taille maximale : 5MB.');
      return;
    }
    
    setIsUploading(true);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    const currentUser = authService.getCurrentUser();
    const userId = currentUser?._id || currentUser?.id;
    if (!userId) {
      alert('Utilisateur non authentifié. Veuillez vous reconnecter.');
      setIsUploading(false);
      return;
    }

    const formData = new FormData();
    formData.append('avatar', file);

    userService.uploadAvatar(userId, formData)
      .then((res) => {
        const uploaded = res?.data || res;
        const url = uploaded?.url || uploaded?.data?.url || null;
        if (url) {
          setPreview(url);
          try {
            const stored = authService.getCurrentUser();
            if (stored) {
              const updated = { ...stored, avatar: uploaded?.data ? uploaded.data : uploaded };
              localStorage.setItem('user', JSON.stringify(updated));
            }
          } catch {}
          dispatch(getCurrentUser());
          onPhotoChange(url);
        } else {
          alert('Upload réussi mais URL introuvable.');
        }
      })
      .catch((err) => {
        alert(err?.message || 'Erreur lors de l\'upload de la photo');
      })
      .finally(() => {
        setIsUploading(false);
      });
  };
  
  // Gérer le clic sur le bouton de sélection
  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };
  
  // Gérer le changement de fichier
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    handleFileSelect(file);
    // Réinitialiser l'input pour permettre de sélectionner le même fichier
    event.target.value = '';
  };
  
  // Gérer le drag & drop
  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };
  
  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragOver(false);
  };
  
  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };
  
  // Supprimer la photo
  const handleRemovePhoto = () => {
    setPreview(null);
    onPhotoChange(null);
  };
  
  return (
    <Box className="profile-photo-uploader">
      <Typography variant="h6" className="uploader-title">
        <FaCamera className="title-icon" />
        Photo de profil
      </Typography>
      
      <Box 
        className={`upload-area ${dragOver ? 'drag-over' : ''} ${preview ? 'has-photo' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Zone d'aperçu de la photo */}
        <Box className="photo-preview">
          {isUploading ? (
            <Box className="uploading-state">
              <CircularProgress size={60} thickness={4} />
              <Typography variant="body2" className="uploading-text">
                Upload en cours...
              </Typography>
            </Box>
          ) : preview ? (
            <Box className="photo-container">
              <Avatar
                src={preview}
                alt="Photo de profil"
                className="profile-avatar"
              >
                <FaUser />
              </Avatar>
              
              {/* Boutons d'action sur la photo */}
              <Box className="photo-actions">
                <Tooltip title="Changer la photo">
                  <IconButton
                    onClick={handleButtonClick}
                    className="action-button change-button"
                    size="small"
                  >
                    <FaCamera />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Supprimer la photo">
                  <IconButton
                    onClick={handleRemovePhoto}
                    className="action-button remove-button"
                    size="small"
                  >
                    <FaTrash />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ) : (
            <Box className="empty-state">
              <FaUpload className="upload-icon" />
              <Typography variant="h6" className="upload-title">
                Ajouter une photo
              </Typography>
              <Typography variant="body2" className="upload-subtitle">
                Glissez-déposez une image ou cliquez pour sélectionner
              </Typography>
              <Typography variant="caption" className="upload-info">
                JPG, PNG ou WebP • Max 5MB
              </Typography>
            </Box>
          )}
        </Box>
        
        {/* Bouton d'upload (visible seulement sans photo) */}
        {!preview && !isUploading && (
          <Button
            variant="outlined"
            startIcon={<FaUpload />}
            onClick={handleButtonClick}
            className="upload-button"
            size="large"
          >
            Sélectionner une photo
          </Button>
        )}
        
        {/* Input file caché */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </Box>
      
      {/* Informations et conseils */}
      <Box className="upload-tips">
        <Typography variant="caption" className="tip-title">
          💡 Conseils pour une bonne photo :
        </Typography>
        <ul className="tips-list">
          <li>Utilisez une photo récente et professionnelle</li>
          <li>Assurez-vous que votre visage est bien visible</li>
          <li>Évitez les photos de groupe ou floues</li>
          <li>Format carré recommandé (1:1)</li>
        </ul>
      </Box>
    </Box>
  );
};

export default ProfilePhotoUploader;